import React, { memo, FC, useEffect, useState, useRef, useContext } from 'react';
import { Box, Divider, Button, Paper, Typography, makeStyles, Theme, Tooltip } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ProductService from 'libs/resources/product/ProductService';
import { ExportAsCSV } from '../products/ExportAsCSV';
import Skeleton from '@material-ui/lab/Skeleton';
import Table, { TableColumn } from 'legacy-components/table/Table';
import { SnackNotificationContext } from 'legacy-components/notifications/GenericSnackNotification';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import CheckCircleOutline from '@material-ui/icons/CheckCircleOutline';
import { GridViewApprovalStatusBadgeCellRenderer } from 'legacy-components/grid-view/GridView';
import {useAuthorization} from "../authorizer/components/AuthorizationProvider";

// Define styles
const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(2)
  },
  paper: {
    padding: theme.spacing(3),
    width: '100%'
  },
  title: {
    fontSize: '1.125rem',
    fontWeight: 500
  },
  button: {
    textTransform: 'none'
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(3)
  },
  cursorPointer: {
    cursor: 'pointer'
  },
  alertMargin: {
    marginBottom: '16px'
  }
}));

// Component for displaying skeleton loader
const SubscriptionGridSkeleton = () => {
  const [values] = useState(Array.from(Array(5).keys()));

  return (
    <>
      <Divider />
      <Box padding="24px" width="100%">
        <Box display="flex" justifyContent="space-between" width="100%">
          <Skeleton width={160} />
          <Skeleton variant="circle" width={32} height={32} />
        </Box>
        <Box marginTop="16px" width="100%" height="100%" padding="24px">
          <Box display="flex" justifyContent="space-between" width="100%" height="40px">
            <Skeleton height={20} width={80} />
            <Box display="flex" width={140} justifyContent="space-between">
              <Skeleton height={20} width={100} />
              <Box height={20} width={80} />
            </Box>
          </Box>
          <Divider />
          <Box width="100%" height="180px">
            {values.map(value => (
              <Box key={value} display="flex" marginTop="16px" justifyContent="space-between" width="100%" height="24px">
                <Skeleton height={20} width={160} />
                <Box display="flex" width={140} justifyContent="space-between">
                  <Skeleton height={20} width={80} />
                  <Skeleton variant="circle" height={20} width={20} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </>
  );
};

// Custom table cell renderer for actions
const ActionCell = (props: { data: any, context: { refreshData: () => void } }) => {
  const notificationContext = useContext(SnackNotificationContext);
  const classes = useStyles();
  
  const SubcribedMessage = () => {
    return <span>Subscription Enabled</span>;
  };
  
  const RevokedMessage = () => {
    return <span>Subscription Revoked</span>;
  };
  
  const updateSubscription = (subscriptionId: string, action: string) => {
    return new Promise((resolve) => {
      ProductService.updateSubscriptionStatus(subscriptionId, action)
        .subscribe(() => {
          if (action === 'APPROVE') {
            notificationContext.show({
              timeout: 6000,
              id: "approve success",
              message: <SubcribedMessage/>
            });
          }
          else if (action === 'REVOKE') {
            notificationContext.show({
              timeout: 6000,
              id: "revoke success",
              message: <RevokedMessage/>
            });
          }
          // Refresh the data after subscription status change
          if (props.context && props.context.refreshData) {
            props.context.refreshData();
          }
          resolve(true);
        });
    });
  };
  const { isActionAllowed } = useAuthorization("SUBSCRIPTION" , props.data.subscriptionId );
  const canUpdate = isActionAllowed("update");
  if (canUpdate && props.data.status === 'SUBSCRIBED') {
    return (   
      <React.Fragment>
        <Tooltip title="Revoke Subscription">
          <RemoveCircleOutlineIcon 
            onClick={() => updateSubscription(props.data.subscriptionId, "REVOKE")}
            className={classes.cursorPointer}
          /> 
        </Tooltip>
      </React.Fragment>
    );
  } else {
    return (   
      <React.Fragment>
        <Tooltip title="Enable Subscription">
          <CheckCircleOutline 
            onClick={() => updateSubscription(props.data.subscriptionId, "APPROVE")}
            className={classes.cursorPointer}
          /> 
        </Tooltip>
      </React.Fragment>
    );
  }
};

interface ServiceSubscriptionViewProps {
  productName: string;
}

// Define subscription interface
interface Subscription {
  id?: string;
  subscriptionId?: string;
  applicationName: string;
  status: string;
  subscriberEmail?: string;
  teamName?: string;
  gatewayName?: string;
  consumerKey?: string;
  sectorName?: string;
}

export const ServiceSubscriptionView: React.FC<ServiceSubscriptionViewProps> = ({ productName }) => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  
  // Get the current identity environment from redux store
  const identityEnvironment = useSelector((state: any) => state?.common?.identityEnvironment);

  // Fetch subscriptions when component mounts or when productName/environment changes
  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get subscriptions using ProductService instead of direct API call - use "detailed" to match ProductSubscriptions
        const subscription = await ProductService.getSubscriptions(productName, 'detailed')
          .toPromise();
        
        // Set the subscription data from the response
        setSubscriptions(subscription || []);
      } catch (err) {
        console.error('Error fetching subscriptions:', err);
        setError('Failed to load subscription data. Please try again.');
        // Set empty array so the UI can still render properly
        setSubscriptions([]);
      } finally {
        setLoading(false);
      }
    };

    if (productName) {
      fetchSubscriptions();
    }
  }, [productName, identityEnvironment]);

  const navigateToNewSubscription = () => {
    history.push(`/home/consumer/products/${productName}/subscribe`);
  };
  
  const exportSubscriptionsAsCSV = () => {
    if (!productName) return;
    
    // Use ProductService.getSubscriptions instead of direct API call
    ProductService.getSubscriptions(productName, "detailed")
      .toPromise()
      .then(response => {
        // Ensure all records have a status
        response.forEach((subscription: any) => {
          if (!subscription.status) {
            subscription.status = "SUBSCRIBED";
          }
        });
        ExportAsCSV(response, "file");
      })
      .catch(error => {
        console.error('Error exporting subscriptions:', error);
      });
  };

  // Process subscription data to match the format in ProductSubscriptions
  const processSubscriptionInfo = (subscriptions: any[]) => {
    return subscriptions.map(item => {
      return {
        subscriptionId: item.subscriptionId,
        applicationName: item.applicationName,
        subscriberEmail: item.subscriberEmail,
        gatewayName: item.gatewayName === "trimble-prod" ? "prod" : "pre-prod",
        teamName: item.teamName,
        sectorName: item.sectorName,
        status: item.status ? item.status : "SUBSCRIBED",
        consumerKey: item.consumerKey
      };
    });
  };
  
  // Render the subscription content or fallback to skeleton
  const renderContent = () => {
    if (loading) {
      return <SubscriptionGridSkeleton />;
    }

    if (error) {
      return (
        <Box p={3}>
          <Alert severity="error">{error}</Alert>
        </Box>
      );
    }

    if (!subscriptions || subscriptions.length === 0) {
      return (
        <Box p={3}>
          <Alert severity="info">
            No subscriptions found for this product. Click the Subscribe button to create a new subscription.
          </Alert>
        </Box>
      );
    }

    const processedData = processSubscriptionInfo(subscriptions);
    
    // Function to refresh data that will be passed to the ActionCell
    const refreshData = () => {
      // Fetch subscriptions data again
      if (productName) {
        ProductService.getSubscriptions(productName, 'detailed')
          .toPromise()
          .then(newSubscriptions => {
            setSubscriptions(newSubscriptions || []);
          })
          .catch(err => {
            console.error('Error refreshing subscriptions:', err);
          });
      }
    };

    return (
      <Box p={2}>
        <Table
          rowData={processedData}
          width="100%"
          height="300px"
          pagination={true}
          context={{ refreshData }}
          frameworkComponents={{
            ActionCell,
            GridViewApprovalStatusBadgeCellRenderer
          }}
        >
          <TableColumn field="applicationName" headerName="Application Name" flex={0.9} />
          <TableColumn field="consumerKey" headerName="Consumer Key" flex={0.9} />
          <TableColumn 
            field="status" 
            headerName="Status" 
            flex={0.3} 
            cellRenderer="GridViewApprovalStatusBadgeCellRenderer" 
          />
          <TableColumn field="subscriberEmail" headerName="Email" flex={0.9} />
          <TableColumn field="teamName" headerName="Team" flex={0.5} />
          <TableColumn field="sectorName" headerName="Sector" flex={0.5} />
          <TableColumn field="actions" headerName="Actions" flex={0.1} cellRenderer="ActionCell" />
        </Table>
      </Box>
    );
  };

  return (
    <Box className={classes.root}>
      <Paper className={classes.paper}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography className={classes.title}>Product Subscriptions</Typography>
          <Button 
            variant="contained" 
            color="primary" 
            className={classes.button}
            onClick={exportSubscriptionsAsCSV}
            disabled={!subscriptions || subscriptions.length === 0}
          >
            Export As CSV
          </Button>
        </Box>
        
        <Alert severity="info" className={classes.alertMargin}>
          <b>Note:</b> The Subscriptions view displays only Trimble Identity v4.0 applications
        </Alert>
        
        {renderContent()}
      </Paper>
    </Box>
  );
};

export default memo(ServiceSubscriptionView);